import { History, Pencil, Trash2, Undo2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Aviso } from '@/components/ui/aviso';
import { Etiqueta } from '@/components/ui/etiqueta';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils/cn';
import { formatoCantidad, formatoPrecio } from '@/lib/utils/formato-precio';
import type {
  Action,
  ConteosDeReglas,
  PricingRule,
  RuleScope,
  TipoDeRegla,
} from '../types/pricing-rules.types';
import {
  alcanceEnFrase,
  camposEnPalabras,
  describirAlcance,
  describirReglaCompleta,
  esTodoElCatalogo,
  estadoDeRegla,
  etiquetaDeRegla,
  lasQueNoCorren,
  numero,
  topesDeMovimiento,
} from '../lib/reglas-en-palabras';
import { nombreAutomatico, tieneNombrePropio } from '../lib/nueva-regla';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';
import { lineaDeQuien } from '@/features/smart-pricing/lib/historial-de-estrategia-en-palabras';

/**
 * La lista de reglas (maqueta S1; pulido, spec 232). Sin grupos, sin prioridad:
 * una fila por regla, agrupadas por qué límite ponen, en la tabla del kit (la
 * misma de Por decidir y Productos). Dentro de cada tipo, las que corren van
 * primero. Los números («lo limitó», «con costo») salen de GET /conteos (plan
 * 4B-datos, Tarea 5); si no llegaron, «—».
 */

type Seccion = TipoDeRegla | 'VIEJAS';

const SECCIONES: Array<{ clave: Seccion; titulo: string; detalle: string }> = [
  {
    clave: 'MARGEN_MINIMO',
    titulo: 'No ganar menos de…',
    detalle:
      'margen mínimo sobre el precio · es la de base: rige donde no hay otra más específica del mismo tipo',
  },
  { clave: 'MOVIMIENTO', titulo: 'No mover más de… por vez', detalle: 'contra el precio de hoy' },
  {
    clave: 'SOBRE_EL_MAS_BARATO',
    titulo: 'No quedar más de… arriba del más barato',
    detalle: 'necesita precios de competencia',
  },
  // Lote B2 (D6, D7): los tres tipos nuevos.
  {
    clave: 'BAJO_EL_MAS_BARATO',
    titulo: 'No quedar más de… debajo del más barato',
    detalle: 'necesita precios de competencia',
  },
  {
    clave: 'PISO_FIJO',
    titulo: 'No vender por debajo de Bs…',
    detalle: 'un monto fijo · sólo para un producto',
  },
  {
    clave: 'TECHO_FIJO',
    titulo: 'No vender por encima de Bs…',
    detalle: 'un monto fijo · sólo para un producto',
  },
  {
    clave: 'REDONDEO',
    titulo: 'Terminar en…',
    detalle: 'redondeo · va al final y siempre cumple el margen mínimo',
  },
  {
    clave: 'VIEJAS',
    titulo: 'Reglas de antes',
    detalle: 'se crearon con la pantalla anterior',
  },
];

export type FiltroAlcance = 'TODAS' | 'CATALOGO' | 'CATEGORIA' | 'MARCA';

export function filtrarPorAlcance(reglas: PricingRule[], f: FiltroAlcance): PricingRule[] {
  switch (f) {
    case 'CATALOGO':
      return reglas.filter((r) => esTodoElCatalogo(r.scope));
    case 'CATEGORIA':
      return reglas.filter((r) => (r.scope?.categories?.length ?? 0) > 0);
    case 'MARCA':
      return reglas.filter((r) => (r.scope?.brands?.length ?? 0) > 0);
    default:
      return reglas;
  }
}

const num = formatoCantidad;

const pctDe = (a: Action[]): number | null =>
  a.length === 1 && 'percent' in a[0] ? (a[0] as { percent: number }).percent : null;

/** El Bs de un piso o techo fijo (única acción de esas reglas). */
const montoFijoDe = (a: Action[]): number | null =>
  a.length === 1 && 'value' in a[0] ? (a[0] as { value: number }).value : null;

/** La especificidad del alcance, como la mide el motor (`elegirRedondeo`): SKU > marca o categoría > catálogo. */
const especificidadDe = (r: PricingRule): number => {
  const s = r.scope ?? {};
  if (s.skus?.length) return 3;
  if (s.brands?.length || s.categories?.length) return 2;
  return 0;
};

/** El nombre con que una regla nombra a otra en «Cuando se encima con otra»: el propio, o el automático (límite · alcance). */
const nombreVisible = (r: PricingRule): string =>
  tieneNombrePropio(r) ? r.name : nombreAutomatico(r.actions, r.scope ?? {});

/** La comparación del motor (`normal`, alcance-regla): sin mayúsculas ni espacios en las puntas. */
const normalDe = (s: string | null | undefined): string => (s ?? '').trim().toLocaleLowerCase('es');

const enComun = (
  xa: string[] | undefined,
  xb: string[] | undefined,
  igual: (v: string, w: string) => boolean = (v, w) => normalDe(v) === normalDe(w)
): boolean => (xa ?? []).some((v) => (xb ?? []).some((w) => igual(v, w)));

/**
 * Si dos reglas pueden alcanzar al mismo producto (entre esas elige una sola
 * `elegirRedondeo`): el de catálogo, a todos; dos puntuales, cuando comparten
 * categoría, marca o producto, o cuando usan dimensiones distintas (una por
 * categoría y otra por marca pueden cruzarse). Sólo son ajenas seguro dos
 * puntuales de la misma dimensión sin valores en común (Sony y LG). Categorías y
 * marcas se comparan sin mayúsculas ni espacios (como `estaEn` del motor); SKU y
 * excepciones, exactos (como `scope.skus.includes` del motor). Las SKUs que una
 * regla excluye restan a una rival que apunta por SKU: si eran todas, no
 * comparten producto.
 */
const compartenZona = (a: PricingRule, b: PricingRule): boolean => {
  const sa = a.scope ?? {};
  const sb = b.scope ?? {};
  const skusQueQuedan = (s: RuleScope, otra: RuleScope): string[] =>
    (s.skus ?? []).filter((sku) => !(otra.excludedSkus ?? []).includes(sku));
  const skusA = skusQueQuedan(sa, sb);
  const skusB = skusQueQuedan(sb, sa);
  if ((sa.skus?.length && skusA.length === 0) || (sb.skus?.length && skusB.length === 0))
    return false;
  if (esTodoElCatalogo(sa) || esTodoElCatalogo(sb)) return true;
  const dimensiones: Array<[string[] | undefined, string[] | undefined]> = [
    [sa.categories, sb.categories],
    [sa.brands, sb.brands],
    [skusA.length ? skusA : undefined, skusB.length ? skusB : undefined],
  ];
  const exacto = (v: string, w: string): boolean => v === w;
  for (const [i, [xa, xb]] of dimensiones.entries()) {
    if (xa?.length && xb?.length && !enComun(xa, xb, i === 2 ? exacto : undefined)) return false;
  }
  return true;
};

/**
 * El mismo alcance (mismas categorías, marcas, SKUs y excepciones), aunque
 * difieran en mayúsculas o espacios: cubren la misma zona y el motor elige una
 * sola entre las dos.
 */
const mismoAlcance = (a: RuleScope, b: RuleScope): boolean => {
  const clave = (v: string[] | undefined): string =>
    JSON.stringify([...(v ?? [])].map(normalDe).sort());
  const claveExacta = (v: string[] | undefined): string => JSON.stringify([...(v ?? [])].sort());
  return (
    clave(a.categories) === clave(b.categories) &&
    clave(a.brands) === clave(b.brands) &&
    claveExacta(a.skus) === claveExacta(b.skus) &&
    claveExacta(a.excludedSkus) === claveExacta(b.excludedSkus)
  );
};

/**
 * Si `a` es más exigente que `b`, como decide el cruce del motor (`cruzar`,
 * rangos.service): piso más alto, techo más bajo. En el movimiento gana la de
 * topes más bajos en cada sentido; la incomparable (más exigente en un sentido
 * y menos en el otro) no cubre a nadie.
 */
function esMasExigente(a: PricingRule, b: PricingRule, tipo: TipoDeRegla): boolean {
  if (tipo === 'MOVIMIENTO') {
    const ta = topesDeMovimiento(a.actions)!;
    const tb = topesDeMovimiento(b.actions)!;
    const subA = ta.subida ?? Number.POSITIVE_INFINITY;
    const subB = tb.subida ?? Number.POSITIVE_INFINITY;
    const bajA = ta.bajada ?? Number.POSITIVE_INFINITY;
    const bajB = tb.bajada ?? Number.POSITIVE_INFINITY;
    return subA <= subB && bajA <= bajB && (subA < subB || bajA < bajB);
  }
  if (tipo === 'PISO_FIJO' || tipo === 'TECHO_FIJO') {
    const va = montoFijoDe(a.actions) ?? 0;
    const vb = montoFijoDe(b.actions) ?? 0;
    return tipo === 'PISO_FIJO' ? va > vb : va < vb;
  }
  const pa = pctDe(a.actions) ?? 0;
  const pb = pctDe(b.actions) ?? 0;
  return tipo === 'MARGEN_MINIMO' ? pa > pb : pa < pb;
}

/** El número grande de la fila: «12 %», «15 % · subir y bajar», «,99». */
export function valorCorto(r: PricingRule): string {
  const e = estadoDeRegla(r);
  if (e.tipo === 'MOVIMIENTO') {
    const t = topesDeMovimiento(r.actions)!;
    if (t.subida != null && t.bajada != null) {
      return t.subida === t.bajada
        ? `${numero(t.subida)} % · subir y bajar`
        : `subir ${numero(t.subida)} % · bajar ${numero(t.bajada)} %`;
    }
    return t.subida != null
      ? `${numero(t.subida)} % · sólo para subir`
      : `${numero(t.bajada!)} % · sólo para bajar`;
  }
  if (e.tipo === 'REDONDEO') {
    const a = r.actions[0] as Extract<Action, { type: 'ROUND_PRICE' }>;
    const m: Record<string, string> = {
      NEAREST_99: ',99',
      NEAREST_90: ',90',
      NEAREST_00: 'entero',
      NEAREST_50: 'múltiplo de 50',
    };
    return a.mode === 'CUSTOM' ? `múltiplo de ${a.customValue ?? '—'}` : m[a.mode];
  }
  // Lote B2 (D6): el monto en Bs, con sus dos decimales.
  if (e.tipo === 'PISO_FIJO' || e.tipo === 'TECHO_FIJO') {
    const a = r.actions[0];
    return 'value' in a ? `Bs ${formatoPrecio(a.value)}` : '—';
  }
  if (e.tipo) {
    const p = pctDe(r.actions);
    return p == null ? '—' : `${numero(p)} %`;
  }
  return describirReglaCompleta(r.actions);
}

/**
 * «Cuando se encima con otra» (maqueta S1). Sólo entre reglas que corren y del
 * mismo tipo. La de todo el catálogo es «la de base»; una más puntual decide en
 * su parte porque es más específica (lote B2, la jerarquía: aunque pida menos,
 * y entonces lo dice). Dos de todo el
 * catálogo del mismo tipo: el cruce se queda con la más exigente (piso más
 * alto, techo más bajo); la que varias cubren a la vez lo dice en plural. Del
 * redondeo corre una sola por producto (`elegirRedondeo`): compite sólo con
 * las que alcanzan a un mismo producto — la que pierde nombra la zona donde la
 * reemplazan (no la suya entera), la de catálogo lista cada puntual en la de
 * ella y dice que en el resto sigue siendo la de base, y las que no comparten
 * productos no se enciman con nadie.
 */
export function encimadas(reglas: PricingRule[], ahora: Date = new Date()): Record<string, string> {
  const out: Record<string, string> = {};
  const corren = reglas.filter((r) => {
    const e = estadoDeRegla(r, ahora);
    return e.corre && e.tipo != null;
  });
  for (const r of corren) {
    const tipo = estadoDeRegla(r, ahora).tipo!;
    if (tipo === 'REDONDEO') {
      const redondeos = corren.filter((o) => estadoDeRegla(o, ahora).tipo === 'REDONDEO');
      if (redondeos.length < 2) {
        out[r.id] = '—';
        continue;
      }
      // `elegirRedondeo` elige una sola entre las que alcanzan al mismo
      // producto: fuera de eso, cada una corre sola en su zona.
      const rivales = redondeos.filter((o) => o.id !== r.id && compartenZona(r, o));
      if (rivales.length === 0) {
        out[r.id] = 'ninguna encima';
        continue;
      }
      // El orden del motor (`elegirRedondeo`): más puntual primero; con el
      // mismo alcance, menor priority; y si empatan, la más vieja.
      const orden = (x: PricingRule, y: PricingRule): number =>
        especificidadDe(y) - especificidadDe(x) ||
        (x.priority ?? 100) - (y.priority ?? 100) ||
        x.createdAt.localeCompare(y.createdAt) ||
        x.id.localeCompare(y.id);
      const alcance = alcanceEnFrase(r.scope ?? {});
      const quienesGanan = rivales.filter((o) => orden(o, r) < 0).sort(orden);
      if (quienesGanan.length === 0) {
        const porPuntualidad = rivales.some((o) => especificidadDe(o) < especificidadDe(r));
        const seCruzan = rivales
          .filter(
            (o) =>
              !esTodoElCatalogo(r.scope) &&
              especificidadDe(o) === especificidadDe(r) &&
              !mismoAlcance(o.scope ?? {}, r.scope ?? {})
          )
          .sort(orden);
        out[r.id] =
          seCruzan.length > 0
            ? `donde se cruza con «${nombreVisible(seCruzan[0])}», decide esta (va primero)`
            : `en ${alcance} decide esta${porPuntualidad ? ' (la más puntual)' : ''}`;
        continue;
      }
      const elegida = quienesGanan[0];
      if (!esTodoElCatalogo(r.scope)) {
        // `elegirRedondeo` sólo la reemplaza donde otra que le gana alcanza al
        // mismo producto: nombra esas zonas, no la propia entera.
        const clausulas: string[] = [];
        const vistos: PricingRule[] = [];
        let hayIdentica = false;
        let hayMasPuntual = false;
        for (const g of quienesGanan) {
          if (vistos.some((v) => mismoAlcance(v.scope ?? {}, g.scope ?? {}))) continue;
          vistos.push(g);
          const n = nombreVisible(g);
          if (mismoAlcance(r.scope ?? {}, g.scope ?? {})) {
            hayIdentica = true;
            clausulas.push(`en ${alcance} decide «${n}»`);
          } else if (especificidadDe(g) > especificidadDe(r)) {
            hayMasPuntual = true;
            clausulas.push(`en ${alcanceEnFrase(g.scope ?? {})} decide «${n}», más puntual`);
          } else {
            clausulas.push(`donde se cruza con «${n}» decide esa (va primero)`);
          }
        }
        if (!hayIdentica)
          clausulas.push(hayMasPuntual ? `en el resto de ${alcance}, esta` : 'en el resto, esta');
        out[r.id] = clausulas.join('; ');
        continue;
      }
      // La de catálogo: en la zona de cada puntual decide ella; en el resto
      // compite con las de catálogo que queden.
      const puntuales = rivales.filter((o) => !esTodoElCatalogo(o.scope)).sort(orden);
      if (puntuales.length === 0) {
        out[r.id] = `en ${alcance} decide «${nombreVisible(elegida)}»`;
        continue;
      }
      // Descarta sólo una de alcance idéntico (la que gana va primero en la
      // lista). Una que se cruza con otra de antes lo avisa: ahí decide la de antes.
      const listadas: PricingRule[] = [];
      const partes: string[] = [];
      for (const p of puntuales) {
        if (listadas.some((g) => mismoAlcance(g.scope ?? {}, p.scope ?? {}))) continue;
        const cruces = listadas.filter((g) => compartenZona(g, p));
        listadas.push(p);
        partes.push(
          `en ${alcanceEnFrase(p.scope ?? {})} decide «${nombreVisible(p)}», más puntual${
            cruces.length > 0
              ? ` (salvo donde se cruza con «${cruces.map(nombreVisible).join('» o «')}», que va antes)`
              : ''
          }`
        );
      }
      const delResto = [...rivales.filter((o) => esTodoElCatalogo(o.scope)), r].sort(orden)[0];
      partes.push(
        delResto.id === r.id
          ? 'en el resto, esta'
          : `en el resto, decide «${nombreVisible(delResto)}»`
      );
      out[r.id] = partes.join('; ');
      continue;
    }
    const mismas = corren.filter((o) => o.id !== r.id && estadoDeRegla(o, ahora).tipo === tipo);
    if (esTodoElCatalogo(r.scope)) {
      const delCatalogo = corren.filter(
        (o) => estadoDeRegla(o, ahora).tipo === tipo && esTodoElCatalogo(o.scope)
      );
      if (delCatalogo.length < 2) {
        out[r.id] = mismas.length > 0 ? 'es la de base' : 'ninguna encima';
        continue;
      }
      // El cruce se queda con la más exigente; las que otra cubre lo dicen.
      // Empatadas o incomparables (movimiento por sentido): cada una «la de
      // base»; la que varias cubren a la vez lo dice en plural.
      const sinCubrir = delCatalogo.filter(
        (o) => !delCatalogo.some((p) => p.id !== o.id && esMasExigente(p, o, tipo))
      );
      out[r.id] = sinCubrir.some((o) => o.id === r.id)
        ? 'es la de base'
        : sinCubrir.length > 1
          ? 'la cubren las más exigentes'
          : `la cubre «${nombreVisible(sinCubrir[0])}», más exigente`;
      continue;
    }
    // Lote B2 (la jerarquía; H7 de la auditoría): contra las del mismo tipo que
    // comparten zona con esta, no sólo las de todo el catálogo. Las de un escalón
    // menor (todo el catálogo; su marca o categoría si esta es de un producto):
    // decide esta, es más específica, aunque pida menos. Las de su mismo escalón:
    // donde se cruzan decide la más exigente (D3). Las de un escalón mayor: en su
    // parte decide esa.
    const cerca = mismas.filter((o) => compartenZona(r, o));
    if (cerca.length === 0) {
      out[r.id] = 'ninguna encima';
      continue;
    }
    const menores = cerca.filter((o) => especificidadDe(o) < especificidadDe(r));
    const iguales = cerca.filter((o) => especificidadDe(o) === especificidadDe(r));
    const mayores = cerca.filter((o) => especificidadDe(o) > especificidadDe(r));
    // Ronda final B2: en movimiento, «para subir decide esta» sólo tiene sentido
    // frente a otra que ponga tope en ese mismo sentido; la que frena sólo la otra
    // dirección no compite con esta en ninguno.
    let menoresTexto = menores;
    let mayoresTexto = mayores;
    if (tipo === 'MOVIMIENTO') {
      const t = topesDeMovimiento(r.actions)!;
      const enElSentido = (o: PricingRule): boolean => {
        const to = topesDeMovimiento(o.actions)!;
        return (t.subida != null && to.subida != null) || (t.bajada != null && to.bajada != null);
      };
      menoresTexto = menores.filter(enElSentido);
      mayoresTexto = mayores.filter(enElSentido);
    }
    const donde = `en ${describirAlcance(r.scope ?? {})}`;
    const partes: string[] = [];
    if (menoresTexto.length === 0 && iguales.length === 0 && mayoresTexto.length === 0) {
      out[r.id] = 'ninguna encima';
      continue;
    }
    if (menoresTexto.length > 0) {
      if (tipo === 'MOVIMIENTO') {
        // El movimiento, por sentido: sólo en el que pone tope.
        const t = topesDeMovimiento(r.actions)!;
        partes.push(
          t.subida != null && t.bajada != null
            ? `${donde} decide esta: es más específica`
            : t.subida != null
              ? `${donde}, para subir decide esta: es más específica`
              : `${donde}, para bajar decide esta: es más específica`
        );
      } else if (tipo === 'PISO_FIJO' || tipo === 'TECHO_FIJO') {
        // Lote B2 (ronda final): el monto fijo compara Bs — gana el piso más
        // alto y el techo más bajo —, no el % (que para estos tipos no existe).
        const mio = montoFijoDe(r.actions) ?? 0;
        const valores = menoresTexto.map((b) => montoFijoDe(b.actions) ?? 0);
        const base = tipo === 'PISO_FIJO' ? Math.max(...valores) : Math.min(...valores);
        const pideMenos = tipo === 'PISO_FIJO' ? mio < base : mio > base;
        partes.push(
          pideMenos
            ? `${donde} decide esta aunque pide menos que Bs ${formatoPrecio(base)}: es más específica`
            : `${donde} decide esta: es más específica`
        );
      } else {
        // Margen: más alto es más exigente. Distancia al más barato: más bajo.
        const mio = pctDe(r.actions) ?? 0;
        const valores = menoresTexto.map((b) => pctDe(b.actions) ?? 0);
        const base = tipo === 'MARGEN_MINIMO' ? Math.max(...valores) : Math.min(...valores);
        const pideMenos = tipo === 'MARGEN_MINIMO' ? mio < base : mio > base;
        partes.push(
          pideMenos
            ? `${donde} decide esta aunque pide menos que ${numero(base)} %: es más específica`
            : `${donde} decide esta: es más específica`
        );
      }
    }
    if (iguales.length > 0) {
      partes.push(
        `donde se cruza con «${iguales.map(nombreVisible).join('» o «')}», decide la más exigente`
      );
    }
    for (const g of mayoresTexto) {
      partes.push(
        `en ${describirAlcance(g.scope ?? {})} decide «${nombreVisible(g)}», más específica`
      );
    }
    if (menoresTexto.length === 0 && iguales.length === 0) partes.push('en el resto, esta');
    out[r.id] = partes.join('; ');
  }
  return out;
}

function aQuienDetalle(r: PricingRule, conteos: ConteosDeReglas | null): string {
  const c = conteos?.reglas.find((x) => x.ruleId === r.id);
  if (!c) return '—';
  const partes = [`${num(c.alcanzados)} ${c.alcanzados === 1 ? 'producto' : 'productos'}`];
  const tipo = estadoDeRegla(r).tipo;
  if (tipo === 'MARGEN_MINIMO' && c.sinCosto > 0) partes.push(`${num(c.sinCosto)} sin costo`);
  if ((tipo === 'SOBRE_EL_MAS_BARATO' || tipo === 'BAJO_EL_MAS_BARATO') && c.alcanzados > 0)
    partes.push(`${Math.round((c.conCompetencia / c.alcanzados) * 100)} % con competencia`);
  if (c.excepciones > 0)
    partes.push(`${c.excepciones} ${c.excepciones === 1 ? 'excepción' : 'excepciones'}`);
  return partes.join(' · ');
}

function loLimito(r: PricingRule, conteos: ConteosDeReglas | null): string {
  if (estadoDeRegla(r).tipo === 'REDONDEO') return 'nunca limita';
  const c = conteos?.reglas.find((x) => x.ruleId === r.id);
  if (!c) return '—';
  const v = c.limitaron.veces;
  return v === 0 ? 'ninguna vez' : v === 1 ? '1 vez' : `${num(v)} veces`;
}

const SIETE_DIAS = 7 * 24 * 60 * 60 * 1000;

/** Spec 294 (D-R4-4): «Sin aplicar · se apaga», «… cambia el límite», etc. */
function etiquetaDelCambio(b: NonNullable<PricingRule['borrador']>): string {
  if (b.tipo === 'NUEVA') return 'Sin aplicar · nueva';
  if (b.tipo === 'BORRAR') return 'Sin aplicar · se borra';
  if (b.enciende === false) return 'Sin aplicar · se apaga';
  if (b.enciende === true) return 'Sin aplicar · se enciende';
  return `Sin aplicar · cambia ${camposEnPalabras(b.campos)}`;
}

/**
 * Spec 294: «Hoy rige: 10 % · encendida» cuando el cambio toca el límite o el
 * encendido (lo que rige hasta aplicar). Una nueva no tiene qué decir.
 */
function hoyRige(r: PricingRule): string | null {
  const b = r.borrador;
  if (!b || b.tipo !== 'CAMBIO' || !r.vigente) return null;
  if (!b.campos.includes('actions') && !b.campos.includes('isActive')) return null;
  const v = r.vigente;
  const comoRige: PricingRule = { ...r, ...v };
  return `Hoy rige: ${valorCorto(comoRige)} · ${v.isActive ? 'encendida' : 'apagada'}`;
}

/**
 * El nombre de la fila: el propio, con el límite en palabras debajo; sin nombre
 * propio, el límite es el nombre («No ganar menos de 15 %», no «15 %» suelto).
 *
 * Compite (rediseño del 14-oct): debajo va la frase entera («No ganar menos de
 * 18 %»), como en la maqueta; el número corto ya está en el chip de la fila.
 */
function nombreEnLaLista(r: PricingRule): { nombre: string; debajo: string | null } {
  return tieneNombrePropio(r)
    ? { nombre: r.name, debajo: describirReglaCompleta(r.actions) }
    : { nombre: describirReglaCompleta(r.actions), debajo: null };
}

/**
 * El chip de la fila (maqueta: «18 %» antes del nombre): el límite en corto,
 * para leer la columna de un vistazo. Es decorativo: la misma información va en
 * palabras al lado (el nombre o la frase de debajo), así que el lector de
 * pantalla no lo repite. Las reglas de antes no tienen un número que mostrar.
 */
export function valorDelChip(r: PricingRule): string | null {
  const tipo = estadoDeRegla(r).tipo;
  if (!tipo) return null;
  if (tipo === 'MOVIMIENTO') {
    const t = topesDeMovimiento(r.actions)!;
    if (t.subida != null && t.bajada != null)
      return t.subida === t.bajada
        ? `±${numero(t.subida)} %`
        : `↑${numero(t.subida)} ↓${numero(t.bajada)} %`;
    return t.subida != null ? `↑${numero(t.subida)} %` : `↓${numero(t.bajada!)} %`;
  }
  if (tipo === 'REDONDEO') {
    const a = r.actions[0] as Extract<Action, { type: 'ROUND_PRICE' }>;
    const m: Record<string, string> = {
      NEAREST_99: ',99',
      NEAREST_90: ',90',
      NEAREST_00: ',00',
      NEAREST_50: '×50',
    };
    return a.mode === 'CUSTOM' ? `×${a.customValue ?? '—'}` : (m[a.mode] ?? null);
  }
  if (tipo === 'PISO_FIJO' || tipo === 'TECHO_FIJO') {
    const v = montoFijoDe(r.actions);
    return v == null ? null : `Bs ${formatoPrecio(v)}`;
  }
  const p = pctDe(r.actions);
  return p == null ? null : `${numero(p)} %`;
}

/** Los botones de sólo ícono de la fila: redondos, con el foco visible del kit. */
const BOTON_DE_ICONO = 'h-8 w-8 rounded-full p-0 text-muted-foreground hover:text-foreground';

export function ListaDeReglas({
  reglas,
  todas,
  conteos,
  onToggle,
  onEditar,
  onBorrar,
  onQuitarFin,
  onDescartarCambio,
  puedeDescartarElCambio = () => true,
  puedeBorrar = () => true,
  onHistorial,
  ahora = new Date(),
}: {
  reglas: PricingRule[];
  /** La lista entera sin filtrar: lo que se encima se mide contra todo el catálogo. */
  todas?: PricingRule[];
  conteos: ConteosDeReglas | null;
  /**
   * Cada acción sale sólo si llega (decisión del 26-sep: un permiso por acción,
   * `usePermisosDeReglas`). Sin el interruptor el estado queda en palabras.
   * Sin ninguna, la lista es de sólo lectura.
   */
  onToggle?: (r: PricingRule) => void;
  onEditar?: (r: PricingRule) => void;
  onBorrar?: (r: PricingRule) => void;
  /**
   * «Quitar la fecha de fin» en una regla vencida (la vigencia suele venir de la
   * migración de grupos). Una regla no tiene dueño: vencida se reactiva, a
   * diferencia de una estrategia. Pide el permiso de editar.
   */
  onQuitarFin?: (r: PricingRule) => void;
  /**
   * Spec 294 (decisión del dueño, 7-oct): «Descartar este cambio» en una regla
   * con un cambio sin aplicar. La página lo pasa con encender o editar; qué
   * fila lo ofrece lo dice `puedeDescartarElCambio` (con sólo editar: si el
   * cambio es sólo de esa persona). Nunca se ofrece lo que el servidor negaría.
   */
  onDescartarCambio?: (r: PricingRule) => void;
  puedeDescartarElCambio?: (r: PricingRule) => boolean;
  /**
   * Spec 294: qué fila ofrece «Borrar» (con `onBorrar`). Borrar una nueva sin
   * aplicar la descarta: si no es sólo de esa persona, el servidor pide encender.
   */
  puedeBorrar?: (r: PricingRule) => boolean;
  /**
   * Spec 294 (D-R4-5): «Historial» abre el historial de cambios de la regla. La
   * página lo pasa siempre (verlo es ver reglas). Una nueva sin aplicar no tiene.
   */
  onHistorial?: (r: PricingRule) => void;
  ahora?: Date;
}) {
  // Soporte («Entrar como») en una demo sólo mira: las acciones quedan a la vista, apagadas.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  const notas = encimadas(todas ?? reglas, ahora);
  const seccion = (r: PricingRule): Seccion => estadoDeRegla(r, ahora).tipo ?? 'VIEJAS';
  const corre = (r: PricingRule) => estadoDeRegla(r, ahora).corre;
  // El porqué largo de las que no corren, una sola vez (maqueta): repetido en
  // cada fila no se leía. En la fila queda la etiqueta corta. Cuenta TODAS las
  // reglas (no sólo las del filtro), como «Cuando se encima con otra» (agregado G).
  const noCorren = lasQueNoCorren(todas ?? reglas, ahora);

  return (
    // Compite (rediseño del 14-oct): la lista va adentro de la tarjeta de la
    // página (la de los filtros), como en la maqueta. Cada tipo es un tramo de
    // esa tarjeta con su título; arriba de todo, el aviso de las que no corren.
    <div>
      {noCorren && (
        <div className="border-t border-border px-5 py-4">
          <Aviso
            tono="alerta"
            titulo={noCorren.titulo}
            data-testid="las-que-no-corren"
            className="rounded-xl text-sm"
          >
            {noCorren.texto}
          </Aviso>
        </div>
      )}
      {SECCIONES.map((s) => {
        // Las que corren primero (cambio aprobado con la maqueta): se ve de entrada
        // qué está corriendo. `filter` conserva el orden de cada mitad.
        const deEsta = reglas.filter((r) => seccion(r) === s.clave);
        const ordenadas = [...deEsta.filter(corre), ...deEsta.filter((r) => !corre(r))];
        if (ordenadas.length === 0) return null;
        return (
          <section key={s.clave} aria-label={s.titulo} className="border-t border-border">
            <h3 className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 px-5 py-3">
              <span className="tracking-tight text-base font-semibold text-foreground">
                {s.titulo}
              </span>
              <span className="text-xs text-muted-foreground">{s.detalle}</span>
            </h3>
            <Table className="min-w-[980px] table-fixed">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[19%]" />
                <col className="w-[10%]" />
                <col className="w-[16%]" />
                <col className="w-[11%]" />
                <col className="w-[14%]" />
              </colgroup>
              <TableHeader className="bg-muted/50">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-10 pl-5 text-xs font-semibold">Regla</TableHead>
                  <TableHead className="h-10 text-xs font-semibold">A quién alcanza</TableHead>
                  <TableHead className="h-10 text-xs font-semibold">Lo limitó (7 días)</TableHead>
                  <TableHead className="h-10 text-xs font-semibold">
                    Cuando se encima con otra
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold">Estado</TableHead>
                  <TableHead className="h-10 pr-5 text-xs">
                    <span className="sr-only">Acciones</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordenadas.map((r) => {
                  const etiqueta = etiquetaDeRegla(r, ahora);
                  const nueva = ahora.getTime() - new Date(r.createdAt).getTime() < SIETE_DIAS;
                  const { nombre, debajo } = nombreEnLaLista(r);
                  const chip = valorDelChip(r);
                  // Spec 294: una que se borra al aplicar sólo se puede dejar como
                  // está (descartar el cambio): encenderla, editarla o volver a
                  // borrarla daría 409.
                  const seBorra = r.borrador?.tipo === 'BORRAR';
                  const rige = hoyRige(r);
                  const quien = lineaDeQuien(r);
                  return (
                    <TableRow
                      key={r.id}
                      data-testid="fila-regla"
                      data-regla={r.id}
                      className={cn(seBorra && 'opacity-70')}
                    >
                      <TableCell className="min-w-0 py-3.5 pl-5">
                        <div className="flex min-w-0 items-start gap-3">
                          {/* El límite en corto (maqueta): decorativo, lo dice el texto de al lado. */}
                          <span
                            aria-hidden="true"
                            data-testid="chip-de-la-regla"
                            className={cn(
                              'tabular-nums flex h-10 min-w-[3.5rem] shrink-0 items-center justify-center whitespace-nowrap rounded-xl px-2.5 text-base font-semibold',
                              corre(r)
                                ? 'bg-primary/10 text-primary'
                                : 'bg-muted text-muted-foreground'
                            )}
                          >
                            {chip ?? '—'}
                          </span>
                          <div className="min-w-0 flex-1 space-y-0.5">
                            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                              <span className="min-w-0 font-semibold text-foreground [overflow-wrap:anywhere]">
                                {nombre}
                              </span>
                              {nueva && !r.borrador && <Etiqueta tono="info">Nueva</Etiqueta>}
                            </div>
                            {debajo && (
                              <div className="min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                                {debajo}
                              </div>
                            )}
                            {quien && (
                              <div className="min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                                {quien}
                              </div>
                            )}
                            {(etiqueta || r.borrador) && (
                              <div className="flex min-w-0 flex-wrap gap-1.5 pt-1">
                                {etiqueta && (
                                  <Etiqueta tono={etiqueta.tono} className="whitespace-normal">
                                    {etiqueta.texto}
                                  </Etiqueta>
                                )}
                                {r.borrador && (
                                  <Etiqueta
                                    tono="alerta"
                                    data-testid="sin-aplicar"
                                    className="min-w-0 whitespace-normal [overflow-wrap:anywhere]"
                                  >
                                    {etiquetaDelCambio(r.borrador)}
                                  </Etiqueta>
                                )}
                              </div>
                            )}
                            {rige && (
                              <div className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
                                {rige}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-3.5">
                        <div className="font-medium text-foreground">
                          {describirAlcance(r.scope ?? {})}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {aQuienDetalle(r, conteos)}
                        </div>
                      </TableCell>
                      <TableCell className="tabular-nums py-3.5 text-sm text-foreground">
                        {loLimito(r, conteos)}
                      </TableCell>
                      <TableCell className="py-3.5 text-xs text-muted-foreground">
                        {notas[r.id] ?? '—'}
                      </TableCell>
                      <TableCell className="py-3.5">
                        {/* El estado en palabras siempre, no sólo el color del interruptor. */}
                        <div className="flex items-center gap-2.5">
                          {onToggle && !seBorra && (
                            <Switch
                              checked={r.isActive}
                              onCheckedChange={() => onToggle(r)}
                              {...sinCambios}
                              aria-label={`Encender o apagar «${r.name}»`}
                            />
                          )}
                          <span
                            className={cn(
                              'text-sm font-medium',
                              r.isActive ? 'text-foreground' : 'text-muted-foreground'
                            )}
                          >
                            {r.isActive ? 'encendida' : 'apagada'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-3.5 pr-5">
                        <div className="flex flex-wrap items-center justify-end gap-1">
                          {onQuitarFin &&
                            !seBorra &&
                            r.validUntil &&
                            new Date(r.validUntil) < ahora && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 rounded-full"
                                onClick={() => onQuitarFin(r)}
                                {...sinCambios}
                              >
                                Quitar la fecha de fin
                              </Button>
                            )}
                          {onDescartarCambio && r.borrador && puedeDescartarElCambio(r) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 gap-1.5 rounded-full"
                              aria-label={`Descartar este cambio de «${r.name}»`}
                              onClick={() => onDescartarCambio(r)}
                              {...sinCambios}
                            >
                              <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                              Descartar este cambio
                            </Button>
                          )}
                          {/* Íconos de la maqueta. El texto queda para el lector de
                              pantalla (y en el `title`, para quien pasa el mouse). */}
                          {onHistorial && r.borrador?.tipo !== 'NUEVA' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className={BOTON_DE_ICONO}
                              aria-label={`Historial de «${r.name}»`}
                              title="Historial"
                              onClick={() => onHistorial(r)}
                            >
                              <History className="h-4 w-4" aria-hidden="true" />
                              <span className="sr-only">Historial</span>
                            </Button>
                          )}
                          {onEditar && !seBorra && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className={BOTON_DE_ICONO}
                              aria-label={`Editar «${r.name}»`}
                              title="Editar"
                              onClick={() => onEditar(r)}
                              {...sinCambios}
                            >
                              <Pencil className="h-4 w-4" aria-hidden="true" />
                              <span className="sr-only">Editar</span>
                            </Button>
                          )}
                          {onBorrar && !seBorra && puedeBorrar(r) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(BOTON_DE_ICONO, 'text-bloqueo hover:text-bloqueo')}
                              aria-label={`Borrar «${r.name}»`}
                              title="Borrar"
                              onClick={() => onBorrar(r)}
                              {...sinCambios}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                              <span className="sr-only">Borrar</span>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </section>
        );
      })}
      <p className="border-t border-border px-5 py-3.5 text-xs leading-relaxed text-muted-foreground">
        Sin orden que acomodar: cuando dos reglas del mismo tipo se enciman, rige la más específica
        (este producto, su marca o categoría, todo el catálogo), aunque pida menos; si alcanzan
        igual de puntual o son de tipos distintos, se cumplen las dos y decide la más exigente. El
        redondeo va aparte: si se enciman dos, corre una sola, la de alcance más puntual.
      </p>
    </div>
  );
}
